import { FC, MouseEvent, ReactNode } from 'react';
import styled from 'styled-components';
import { sanitizeHtml } from 'utilities';

import { Icon, IconTypesValues, iconSizes } from '../Icon';
import { Question } from '../../../assets/PuzzleTable/Question';
import { Text } from '../../Typhography';
import { ModalWrapper } from '../../../modals/styled';
import { useModalProvider } from '../../../context/ModalProviderContext';

const IconWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const RejectReasonText = styled(Text)`
  margin-block: 20px;
  text-align: center;
  font-size: 14px;
`;

export enum InformationModalTypes {
  Info = 'info',
  Error = 'error',
  Warning = 'warning',
}

interface ModalProps {
  type: InformationModalTypes;
  icon: IconTypesValues;
  title: string;
  content: React.ReactNode;
  close: () => void;
}

export interface InformationModalProps {
  type?: InformationModalTypes;
  show?: boolean;
  icon?: IconTypesValues;
  title: string;
  content?: ReactNode;
  onClick?: (e: MouseEvent<HTMLSpanElement>) => void;
}

const Modal = ({ type, icon, title, content, close }: ModalProps) => {
  const modalData = {
    [InformationModalTypes.Error]: {
      iconColor: 'rgb(209, 0, 0)',
    },
    [InformationModalTypes.Warning]: {
      iconColor: 'rgb(248, 176, 55)',
    },
  }[type as InformationModalTypes.Error];

  return (
    <ModalWrapper>
      <IconWrapper>
        <div />
        <Icon
          color={modalData.iconColor}
          type={icon}
          width={iconSizes.large}
          height={iconSizes.large}
        />
        <Icon
          style={{ cursor: 'pointer' }}
          onClick={() => close()}
          type="close"
          width={20}
          height={20}
        />
      </IconWrapper>
      <div>
        <Text weight="bold">{title}</Text>
        <RejectReasonText
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(content as string) }}
        ></RejectReasonText>
      </div>
    </ModalWrapper>
  );
};

export const InformationModal = ({
  type = InformationModalTypes.Info,
  show = true,
  icon = 'exclamationMark',
  title,
  content,
  onClick,
}: InformationModalProps) => {
  const { openModal, closeModal } = useModalProvider();

  const openRejectMessageInfoModal = (e: MouseEvent<HTMLSpanElement>) => {
    onClick?.(e);

    openModal('reject-message-modal', Modal as FC, {
      type,
      icon,
      title,
      content,
      close: () => closeModal('reject-message-modal'),
    });
  };

  return show ? <Question onClick={openRejectMessageInfoModal} /> : null;
};
